// ============================================================================
// On-screen 두벌식 (2-set) Hangul keyboard (spec §7.4). Mouse/touch/keyboard
// operable, theme-aware (uses design tokens), and drives the shared controlled
// input field through the tested composition engine so grading is identical
// whether the learner types natively or taps the on-screen keys.
// ============================================================================

import { HangulComposer } from '@core/hangul/compose.js';

// Standard 두벌식 layout. Each key: [base jamo, shifted jamo|null].
const ROWS = [
  [['ㅂ', 'ㅃ'], ['ㅈ', 'ㅉ'], ['ㄷ', 'ㄸ'], ['ㄱ', 'ㄲ'], ['ㅅ', 'ㅆ'],
   ['ㅛ', null], ['ㅕ', null], ['ㅑ', null], ['ㅐ', 'ㅒ'], ['ㅔ', 'ㅖ']],
  [['ㅁ', null], ['ㄴ', null], ['ㅇ', null], ['ㄹ', null], ['ㅎ', null],
   ['ㅗ', null], ['ㅓ', null], ['ㅏ', null], ['ㅣ', null]],
  [['ㅋ', null], ['ㅌ', null], ['ㅊ', null], ['ㅍ', null],
   ['ㅠ', null], ['ㅜ', null], ['ㅡ', null]],
];

/**
 * @param {object} opts
 * @param {HTMLInputElement} opts.input  the shared controlled input field
 * @param {(value:string)=>void} [opts.onChange]
 * @returns {{ el: HTMLElement, sync: ()=>void, clear: ()=>void }}
 */
export function createHangulKeyboard({ input, onChange } = {}) {
  const composer = new HangulComposer(input?.value || '');
  let shifted = false;

  const el = document.createElement('div');
  el.className = 'kbd';
  el.setAttribute('role', 'group');
  el.setAttribute('aria-label', 'On-screen Korean (두벌식) keyboard');

  const push = () => {
    if (input) input.value = composer.value;
    onChange?.(composer.value);
  };

  const tap = (jamo) => {
    composer.input(jamo);
    shifted = false;
    render();
    push();
    input?.focus();
  };

  function keyButton(label, onClick, extraClass = '', wide = false) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `kbd-key ${extraClass}`.trim();
    if (wide) b.classList.add('kbd-key--wide');
    b.textContent = label;
    b.setAttribute('aria-label', label);
    b.addEventListener('click', onClick);
    return b;
  }

  function render() {
    el.innerHTML = '';
    for (const row of ROWS) {
      const r = document.createElement('div');
      r.className = 'kbd-row';
      for (const [base, shift] of row) {
        const label = shifted && shift ? shift : base;
        r.appendChild(keyButton(label, () => tap(label)));
      }
      el.appendChild(r);
    }
    // Control row: Shift, Space, Backspace.
    const ctrl = document.createElement('div');
    ctrl.className = 'kbd-row';
    const shiftKey = keyButton('⇧', () => { shifted = !shifted; render(); },
      shifted ? 'kbd-key--active' : '', true);
    shiftKey.setAttribute('aria-pressed', String(shifted));
    const spaceKey = keyButton('space', () => tap(' '), '', true);
    spaceKey.classList.add('kbd-key--space');
    const backKey = keyButton('⌫', () => { composer.backspace(); render(); push(); input?.focus(); },
      '', true);
    ctrl.append(shiftKey, spaceKey, backKey);
    el.appendChild(ctrl);
  }

  // Keep the composer in step when the field is edited natively (OS Korean IME).
  const sync = () => composer.reset(input?.value || '');
  input?.addEventListener('input', (e) => {
    if (e.isComposing) return;
    if (input.value !== composer.value) sync();
  });

  const clear = () => { composer.reset(''); if (input) input.value = ''; onChange?.(''); render(); };

  render();
  return { el, sync, clear };
}
